import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Button,
  Input,
  Field,
  Card,
  CardHeader,
  Badge,
  Spinner,
  InfoButton,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Checkbox,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../Common/ScrollableMessageBar';
import { useScrollableMessage } from '../../hooks/useScrollableMessage';
import {
  Search24Regular,
  Filter24Regular,
  Info24Regular,
  Warning24Regular,
  CheckmarkCircle24Regular,
  ErrorCircle24Regular,
  Eye24Regular,
  MoreHorizontal24Regular,
} from '@fluentui/react-icons';
import { gfcPolicyService, ExistingTeamsPolicy } from '../../services/gfcPolicyService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.margin('0', '0', '12px', '0'),
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  searchInput: {
    flex: 1,
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('8px'),
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  filterChip: {
    cursor: 'pointer',
  },
  summaryCard: {
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    ...shorthands.gap('16px'),
    ...shorthands.padding('16px'),
  },
  summaryItem: {
    textAlign: 'center',
  },
  policiesTable: {
    ...shorthands.margin('16px', '0'),
  },
  policyRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  selectedRow: {
    backgroundColor: '#f0f8ff',
    borderLeft: '3px solid #106ebe',
  },
  policyName: {
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '14px',
  },
  statusBadge: {
    minWidth: '80px',
  },
  compatibilityIcon: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('24px'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('48px'),
    textAlign: 'center',
    color: '#605e5c',
  },
  selectionInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px'),
    backgroundColor: '#f0f8ff',
    ...shorthands.border('1px', 'solid', '#106ebe'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.margin('16px', '0'),
  },
});

interface ExistingPolicySelectorProps {
  selectedPolicy?: ExistingTeamsPolicy | null;
  onPolicySelect: (policy: ExistingTeamsPolicy | null) => void;
  showSelection?: boolean;
  allowMultiple?: boolean;
}

export const ExistingPolicySelector: React.FC<ExistingPolicySelectorProps> = ({
  selectedPolicy,
  onPolicySelect,
  showSelection = true,
  allowMultiple = false,
}) => {
  const styles = useStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useScrollableMessage<string | null>(null);
  const [policies, setPolicies] = useState<ExistingTeamsPolicy[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const loadExistingPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await gfcPolicyService.getExistingTeamsPolicies();
      setPolicies(result.policies);
      setSummary(result.summary);
    } catch (err) {
      console.error('Failed to load existing Teams policies:', err);
      setError(err instanceof Error ? err.message : 'Failed to load existing policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExistingPolicies();
  }, []);

  const filterOptions = [
    { key: 'gfcCompatible', label: 'GFC Compatible', color: 'success' as const },
    { key: 'notGfcCompatible', label: 'Not GFC Compatible', color: 'danger' as const },
    { key: 'admManaged', label: 'ADM Managed', color: 'brand' as const },
    { key: 'externalAccess', label: 'External Access', color: 'important' as const },
    { key: 'hasAssignments', label: 'Has User Assignments', color: 'warning' as const },
  ];

  const filteredPolicies = policies.filter(policy => {
    // Exclude Global policy from selection
    if (policy.identity === 'Global') {
      return false;
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        policy.identity.toLowerCase().includes(searchLower) ||
        policy.displayName?.toLowerCase().includes(searchLower) ||
        policy.policyType.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Category filters
    if (selectedFilters.length > 0) {
      const matchesFilter = selectedFilters.some(filter => {
        switch (filter) {
          case 'gfcCompatible':
            return policy.gfcCompatible;
          case 'notGfcCompatible':
            return !policy.gfcCompatible;
          case 'admManaged':
            return policy.isADMManaged;
          case 'externalAccess':
            return policy.policyType === 'ExternalAccess';
          case 'hasAssignments':
            return (policy.assignedUsers && policy.assignedUsers > 0) || 
                   (policy.assignedGroups && policy.assignedGroups.length > 0);
          default:
            return true;
        }
      });
      
      if (!matchesFilter) return false;
    }

    return true;
  });

  const toggleFilter = (filterKey: string) => {
    setSelectedFilters(prev => 
      prev.includes(filterKey) 
        ? prev.filter(f => f !== filterKey)
        : [...prev, filterKey]
    );
  };

  const getCompatibilityIcon = (policy: ExistingTeamsPolicy) => {
    if (policy.gfcCompatible) {
      return (
        <div className={styles.compatibilityIcon} data-testid="gfc-existing-policy-compatibility-compatible">
          <CheckmarkCircle24Regular style={{ color: '#107C10' }} />
          <Text size={200} data-testid="gfc-existing-policy-compatibility-compatible-text">Compatible</Text>
        </div>
      );
    } else {
      return (
        <div className={styles.compatibilityIcon} data-testid="gfc-existing-policy-compatibility-incompatible">
          <ErrorCircle24Regular style={{ color: '#d13438' }} />
          <Text size={200} data-testid="gfc-existing-policy-compatibility-incompatible-text">Not Compatible</Text>
        </div>
      );
    }
  };

  const getPolicyTypeColor = (type: string) => {
    switch (type) {
      case 'ExternalAccess':
        return 'success';
      case 'CallingPolicy':
        return 'warning';
      case 'MeetingPolicy':
        return 'important';
      default:
        return 'subtle';
    }
  };

  const handlePolicyClick = (policy: ExistingTeamsPolicy) => {
    if (selectedPolicy?.identity === policy.identity) {
      onPolicySelect(null); // Deselect if clicking the same policy
    } else {
      onPolicySelect(policy);
    }
  };

  if (loading) {
    return (
      <div className={styles.container} data-testid="gfc-existing-policy-selector-loading">
        <div className={styles.loadingContainer} data-testid="gfc-existing-policy-loading-container">
          <Spinner size="large" data-testid="gfc-existing-policy-loading-spinner" />
          <Text data-testid="gfc-existing-policy-loading-title">Discovering existing Teams policies...</Text>
          <Text size={300} style={{ color: '#605e5c' }} data-testid="gfc-existing-policy-loading-description">
            This may take a moment as we scan your tenant for existing policies
          </Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container} data-testid="gfc-existing-policy-selector-error">
        <MessageBar intent="error" data-testid="gfc-existing-policy-error-bar">
          <MessageBarBody data-testid="gfc-existing-policy-error-body">
            <Text weight="semibold" data-testid="gfc-existing-policy-error-title">Failed to Load Existing Policies</Text>
            <br data-testid="gfc-existing-policy-error-break" />
            <Text data-testid="gfc-existing-policy-error-text">{error}</Text>
          </MessageBarBody>
        </MessageBar>
        <Button onClick={loadExistingPolicies} data-testid="gfc-existing-policy-selector-retry">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="gfc-existing-policy-selector">
      <Field
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="gfc-existing-policy-label-row">
            <Text weight="semibold" data-testid="gfc-existing-policy-label">Existing Teams Policies</Text>
            <InfoButton
              info={
                <div style={{ maxWidth: '400px' }} data-testid="gfc-existing-policy-info-content">
                  <Text size={200} data-testid="gfc-existing-policy-info-text">
                    <strong data-testid="gfc-existing-policy-info-title">Policy Discovery</strong><br data-testid="gfc-existing-policy-info-break" />
                    Review existing Teams policies in your tenant to avoid duplicates and 
                    identify opportunities for GFC migration. Select a policy to see detailed 
                    information and migration recommendations.
                  </Text>
                </div>
              }
            />
          </div>
        }
        data-testid="gfc-existing-policy-field"
      >
        <div data-testid="gfc-existing-policy-content">
          {/* Summary Card */}
          {summary && (
            <Card className={styles.summaryCard} data-testid="gfc-existing-policy-summary-card">
              <CardHeader
                header={<Text weight="semibold" data-testid="gfc-existing-policy-summary-title">Policy Overview</Text>}
              />
              <div className={styles.summaryGrid} data-testid="gfc-existing-policy-summary-grid">
                <div className={styles.summaryItem} data-testid="gfc-existing-policy-summary-total">
                  <Text size={700} weight="bold" style={{ color: '#106ebe', display: 'block' }} data-testid="gfc-existing-policy-summary-total-value">
                    {summary.totalPolicies}
                  </Text>
                  <Text size={300} data-testid="gfc-existing-policy-summary-total-label">Total Policies</Text>
                </div>
                <div className={styles.summaryItem} data-testid="gfc-existing-policy-summary-compatible">
                  <Text size={700} weight="bold" style={{ color: '#107C10', display: 'block' }} data-testid="gfc-existing-policy-summary-compatible-value">
                    {summary.gfcCompatible}
                  </Text>
                  <Text size={300} data-testid="gfc-existing-policy-summary-compatible-label">GFC Compatible</Text>
                </div>
                <div className={styles.summaryItem} data-testid="gfc-existing-policy-summary-adm-managed">
                  <Text size={700} weight="bold" style={{ color: '#ff8c00', display: 'block' }} data-testid="gfc-existing-policy-summary-adm-managed-value">
                    {summary.admManaged}
                  </Text>
                  <Text size={300} data-testid="gfc-existing-policy-summary-adm-managed-label">ADM Managed</Text>
                </div>
                <div className={styles.summaryItem} data-testid="gfc-existing-policy-summary-external-access">
                  <Text size={700} weight="bold" style={{ color: '#d13438', display: 'block' }} data-testid="gfc-existing-policy-summary-external-access-value">
                    {summary.externalAccess}
                  </Text>
                  <Text size={300} data-testid="gfc-existing-policy-summary-external-access-label">External Access</Text>
                </div>
              </div>
            </Card>
          )}

          {/* Search and Filters */}
          <div className={styles.searchContainer} data-testid="gfc-existing-policy-search-container">
            <Input
              className={styles.searchInput}
              placeholder="Search policies by name, type, or identity..."
              value={searchTerm}
              onChange={(e, data) => setSearchTerm(data.value)}
              contentBefore={<Search24Regular />}
              data-testid="gfc-existing-policy-search-input"
            />
            <Button
              appearance="subtle"
              icon={<Filter24Regular />}
              data-testid="gfc-existing-policy-filter-button"
            >
              Filter
            </Button>
          </div>

          <div className={styles.filtersContainer} data-testid="gfc-existing-policy-filters-container">
            {filterOptions.map((option, index) => (
              <Badge
                key={option.key}
                appearance={selectedFilters.includes(option.key) ? "filled" : "outline"}
                color={option.color}
                className={styles.filterChip}
                onClick={() => toggleFilter(option.key)}
                data-testid={`gfc-existing-policy-filter-chip-${index}`}
              >
                {option.label}
              </Badge>
            ))}
            {selectedFilters.length > 0 && (
              <Button
                appearance="subtle"
                size="small"
                onClick={() => setSelectedFilters([])}
                data-testid="gfc-existing-policy-clear-filters"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Selection Info */}
          {showSelection && selectedPolicy && (
            <div className={styles.selectionInfo} data-testid="gfc-existing-policy-selection-info">
              <div data-testid="gfc-existing-policy-selection-label">
                <Text weight="semibold" data-testid="gfc-existing-policy-selected-label">Selected Policy: </Text>
                <Text className={styles.policyName} data-testid="gfc-existing-policy-selected-name">
                  {selectedPolicy.displayName || selectedPolicy.identity}
                </Text>
              </div>
              <div style={{ display: 'flex', gap: '8px' }} data-testid="gfc-existing-policy-selection-actions">
                {getCompatibilityIcon(selectedPolicy)}
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => onPolicySelect(null)}
                  data-testid="gfc-existing-policy-clear-selection"
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Policies Table */}
          {filteredPolicies.length === 0 ? (
            <div className={styles.emptyState} data-testid="gfc-existing-policy-empty-state">
              <Info24Regular style={{ fontSize: '48px' }} />
              <Text weight="semibold" size={500} data-testid="gfc-existing-policy-empty-title">
                {searchTerm || selectedFilters.length > 0 ? 'No policies match your criteria' : 'No existing policies found'}
              </Text>
              <Text size={300} data-testid="gfc-existing-policy-empty-description">
                {searchTerm || selectedFilters.length > 0 
                  ? 'Try adjusting your search or filters'
                  : 'Your tenant doesn\'t have any existing Teams policies, or you may not have permissions to view them'
                }
              </Text>
            </div>
          ) : (
            <Table className={styles.policiesTable} data-testid="gfc-existing-policy-table">
              <TableHeader data-testid="gfc-existing-policy-table-header">
                <TableRow data-testid="gfc-existing-policy-table-header-row">
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-policy">Policy</TableHeaderCell>
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-type">Type</TableHeaderCell>
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-compatible">GFC Compatible</TableHeaderCell>
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-management">Management</TableHeaderCell>
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-assignments">Assignments</TableHeaderCell>
                  <TableHeaderCell data-testid="gfc-existing-policy-table-header-actions">Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="gfc-existing-policy-table-body">
                {filteredPolicies.map((policy, index) => (
                  <TableRow 
                    key={policy.identity}
                    className={mergeClasses(
                      styles.policyRow,
                      selectedPolicy?.identity === policy.identity && styles.selectedRow
                    )}
                    onClick={() => handlePolicyClick(policy)}
                    data-testid={`gfc-existing-policy-table-row-${index}`}
                  >
                    <TableCell data-testid={`gfc-existing-policy-table-cell-policy-${index}`}>
                      <div data-testid={`gfc-existing-policy-row-identity-${index}`}>
                        <Text className={styles.policyName} weight="medium" data-testid={`gfc-existing-policy-row-display-name-${index}`}>
                          {policy.displayName || policy.identity}
                        </Text>
                        {policy.displayName && (
                          <Text size={200} style={{ display: 'block', color: '#605e5c' }} data-testid={`gfc-existing-policy-row-identity-text-${index}`}>
                            {policy.identity}
                          </Text>
                        )}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`gfc-existing-policy-table-cell-type-${index}`}>
                      <Badge 
                        appearance="outline" 
                        color={getPolicyTypeColor(policy.policyType) as any}
                        className={styles.statusBadge}
                        data-testid={`gfc-existing-policy-row-type-badge-${index}`}
                      >
                        {policy.policyType}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`gfc-existing-policy-table-cell-compatible-${index}`}>
                      {getCompatibilityIcon(policy)}
                    </TableCell>
                    <TableCell data-testid={`gfc-existing-policy-table-cell-management-${index}`}>
                      <div data-testid={`gfc-existing-policy-row-management-${index}`}>
                        <Text size={300} data-testid={`gfc-existing-policy-row-management-text-${index}`}>
                          {policy.isADMManaged ? 'ADM Managed' : policy.managedByOther || 'Manual'}
                        </Text>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`gfc-existing-policy-table-cell-assignments-${index}`}>
                      <div data-testid={`gfc-existing-policy-row-assignments-${index}`}>
                        {policy.assignedUsers && policy.assignedUsers > 0 && (
                          <Text size={200} style={{ display: 'block' }} data-testid={`gfc-existing-policy-row-assigned-users-${index}`}>
                            {policy.assignedUsers} users
                          </Text>
                        )}
                        {policy.assignedGroups && policy.assignedGroups.length > 0 && (
                          <Text size={200} style={{ display: 'block' }} data-testid={`gfc-existing-policy-row-assigned-groups-${index}`}>
                            {policy.assignedGroups.length} groups
                          </Text>
                        )}
                        {(!policy.assignedUsers || policy.assignedUsers === 0) && 
                         (!policy.assignedGroups || policy.assignedGroups.length === 0) && (
                          <Text size={200} style={{ color: '#605e5c' }} data-testid={`gfc-existing-policy-row-no-assignments-${index}`}>No assignments</Text>
                        )}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`gfc-existing-policy-table-cell-actions-${index}`}>
                      <Menu data-testid={`gfc-existing-policy-row-menu-${index}`}>
                        <MenuTrigger disableButtonEnhancement data-testid={`gfc-existing-policy-row-menu-trigger-${index}`}>
                          <Button
                            appearance="subtle"
                            icon={<MoreHorizontal24Regular />}
                            size="small"
                            data-testid={`gfc-existing-policy-row-actions-button-${index}`}
                          />
                        </MenuTrigger>
                        <MenuPopover data-testid={`gfc-existing-policy-row-menu-popover-${index}`}>
                          <MenuList data-testid={`gfc-existing-policy-row-menu-list-${index}`}>
                            <MenuItem icon={<Eye24Regular />} data-testid={`gfc-existing-policy-row-view-details-${index}`}>
                              View Details
                            </MenuItem>
                            {policy.gfcCompatible && (
                              <MenuItem data-testid={`gfc-existing-policy-row-migrate-gfc-${index}`}>
                                Migrate to GFC
                              </MenuItem>
                            )}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Field>

      {/* Warning about policy conflicts */}
      {policies.some(p => p.policyType === 'ExternalAccess' && !p.isADMManaged) && (
        <MessageBar intent="warning" data-testid="gfc-existing-policy-conflict-warning-bar">
          <MessageBarBody data-testid="gfc-existing-policy-conflict-warning-body">
            <Text weight="semibold" data-testid="gfc-existing-policy-conflict-warning-title">Potential Policy Conflicts</Text>
            <br data-testid="gfc-existing-policy-conflict-warning-break" />
            <Text data-testid="gfc-existing-policy-conflict-warning-text">
              Some existing External Access policies are not managed by ADM. Creating new GFC policies 
              may conflict with these existing policies. Consider reviewing and consolidating your 
              policy management approach.
            </Text>
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};